import React, { useState, useEffect } from 'react';
import { 
  Plus, 
  Webhook, 
  Edit3, 
  Trash2, 
  ExternalLink,
  CheckCircle,
  AlertCircle,
  Clock,
  Copy,
  Eye,
  EyeOff,
  Settings
} from 'lucide-react';
import { CreateWebhookModal } from './CreateWebhookModal';
import platformApi from '../services/platformApi';

interface Webhook {
  id: string;
  name: string;
  url: string;
  secret: string;
  status: string;
  events: string[];
  retryCount: number;
  timeoutMs: number;
  createdAt: string;
  lastSuccessAt?: string;
  lastFailureAt?: string;
  failureCount: number;
}

export const WebhooksPage: React.FC = () => {
  const [webhooks, setWebhooks] = useState<Webhook[]>([]);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [visibleSecrets, setVisibleSecrets] = useState<Set<string>>(new Set());
  const [copiedSecret, setCopiedSecret] = useState<string | null>(null);

  useEffect(() => {
    loadWebhooks();
  }, []);

  const loadWebhooks = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await platformApi.getWebhooks();
      setWebhooks(response.webhooks || []);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load webhooks');
      console.error('Failed to load webhooks:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateWebhook = async (webhookData: any) => {
    try {
      await platformApi.createWebhook(webhookData);
      await loadWebhooks(); // Reload the list
      setShowCreateModal(false);
    } catch (err) {
      console.error('Failed to create webhook:', err);
      throw err; // Let the modal handle the error
    }
  };

  const handleDeleteWebhook = async (webhookId: string) => {
    if (!window.confirm('Are you sure you want to delete this webhook? This action cannot be undone.')) {
      return;
    }

    try {
      await platformApi.deleteWebhook(webhookId);
      await loadWebhooks(); // Reload the list
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete webhook');
      console.error('Failed to delete webhook:', err);
    }
  };

  const toggleSecretVisibility = (webhookId: string) => {
    const newVisible = new Set(visibleSecrets);
    if (newVisible.has(webhookId)) {
      newVisible.delete(webhookId);
    } else {
      newVisible.add(webhookId);
    }
    setVisibleSecrets(newVisible);
  };

  const copySecret = async (secret: string, webhookId: string) => {
    try {
      await navigator.clipboard.writeText(secret);
      setCopiedSecret(webhookId);
      setTimeout(() => setCopiedSecret(null), 2000);
    } catch (err) {
      console.error('Failed to copy secret:', err);
    }
  };

  const maskSecret = (secret: string) => {
    if (secret.length <= 8) return secret;
    return secret.substring(0, 8) + '•'.repeat(20) + secret.substring(secret.length - 4);
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active': return 'text-success bg-green-50 dark:bg-green-900/20';
      case 'inactive': return 'text-light-text-secondary dark:text-dark-text-secondary bg-gray-50 dark:bg-gray-900/20';
      case 'failed': return 'text-error bg-red-50 dark:bg-red-900/20';
      default: return 'text-light-text-secondary dark:text-dark-text-secondary bg-gray-50 dark:bg-gray-900/20';
    }
  };

  const getHealthStatus = (webhook: Webhook) => {
    if (webhook.failureCount === 0 && webhook.lastSuccessAt) {
      return { status: 'healthy', color: 'text-success', text: 'Healthy' };
    } else if (webhook.failureCount > 0 && webhook.failureCount < 5) {
      return { status: 'warning', color: 'text-warning', text: `${webhook.failureCount} recent failures` };
    } else if (webhook.failureCount >= 5) {
      return { status: 'failing', color: 'text-error', text: 'Failing' };
    } else {
      return { status: 'unknown', color: 'text-light-text-secondary dark:text-dark-text-secondary', text: 'No deliveries yet' };
    }
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="text-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
          <p className="mt-4 text-light-text-secondary dark:text-dark-text-secondary">Loading webhooks...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Error Alert */}
      {error && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl p-4">
          <div className="flex items-center space-x-3">
            <AlertCircle className="w-5 h-5 text-red-500" />
            <div>
              <h3 className="font-medium text-red-900 dark:text-red-100">
                Error loading webhooks
              </h3>
              <p className="text-sm text-red-700 dark:text-red-200 mt-1">
                {error}
              </p>
              <button 
                onClick={loadWebhooks}
                className="text-sm text-red-600 dark:text-red-400 hover:text-red-800 dark:hover:text-red-200 mt-2"
              >
                Try again
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-light-text-primary dark:text-dark-text-primary">
            Webhooks
          </h1>
          <p className="text-light-text-secondary dark:text-dark-text-secondary mt-2">
            Receive real-time notifications when events occur in your video rooms
          </p>
        </div>
        <button
          onClick={() => setShowCreateModal(true)}
          className="flex items-center px-4 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600 transition-colors"
        >
          <Plus size={20} className="mr-2" />
          Create Webhook
        </button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="bg-light-bg-secondary dark:bg-dark-bg-secondary rounded-xl p-6 border border-light-border dark:border-dark-border">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-light-text-secondary dark:text-dark-text-secondary">
                Total Webhooks
              </p>
              <p className="text-2xl font-bold text-light-text-primary dark:text-dark-text-primary mt-1">
                {webhooks.length}
              </p>
            </div>
            <Webhook className="w-8 h-8 text-primary-500" />
          </div>
        </div>
        
        <div className="bg-light-bg-secondary dark:bg-dark-bg-secondary rounded-xl p-6 border border-light-border dark:border-dark-border">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-light-text-secondary dark:text-dark-text-secondary">
                Active Webhooks
              </p>
              <p className="text-2xl font-bold text-light-text-primary dark:text-dark-text-primary mt-1">
                {webhooks.filter(w => w.status === 'active').length}
              </p>
            </div>
            <CheckCircle className="w-8 h-8 text-success" />
          </div>
        </div>
        
        <div className="bg-light-bg-secondary dark:bg-dark-bg-secondary rounded-xl p-6 border border-light-border dark:border-dark-border">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-light-text-secondary dark:text-dark-text-secondary">
                Healthy Webhooks
              </p>
              <p className="text-2xl font-bold text-light-text-primary dark:text-dark-text-primary mt-1">
                {webhooks.filter(w => w.failureCount === 0).length}
              </p>
            </div>
            <Clock className="w-8 h-8 text-info" />
          </div>
        </div>
      </div>

      {/* Webhooks List */}
      <div className="bg-light-bg-secondary dark:bg-dark-bg-secondary rounded-xl border border-light-border dark:border-dark-border overflow-hidden">
        <div className="px-6 py-4 border-b border-light-border dark:border-dark-border">
          <h2 className="text-lg font-semibold text-light-text-primary dark:text-dark-text-primary">
            Your Webhooks
          </h2>
        </div>
        
        {webhooks.length === 0 ? (
          <div className="p-12 text-center">
            <Webhook className="w-12 h-12 text-light-text-secondary dark:text-dark-text-secondary mx-auto mb-4" />
            <h3 className="text-lg font-medium text-light-text-primary dark:text-dark-text-primary mb-2">
              No webhooks configured
            </h3>
            <p className="text-light-text-secondary dark:text-dark-text-secondary mb-6">
              Get started by creating your first webhook to receive real-time notifications
            </p>
            <button
              onClick={() => setShowCreateModal(true)}
              className="inline-flex items-center px-4 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600 transition-colors"
            >
              <Plus size={20} className="mr-2" />
              Create Your First Webhook
            </button>
          </div>
        ) : (
          <div className="divide-y divide-light-border dark:divide-dark-border">
            {webhooks.map((webhook) => {
              const healthStatus = getHealthStatus(webhook);
              return (
                <div key={webhook.id} className="p-6 hover:bg-light-bg-tertiary dark:hover:bg-dark-bg-tertiary transition-colors">
                  <div className="flex items-start justify-between">
                    <div className="flex-1 min-w-0">
                      {/* Header */}
                      <div className="flex items-center space-x-3 mb-3">
                        <h3 className="text-lg font-medium text-light-text-primary dark:text-dark-text-primary truncate">
                          {webhook.name}
                        </h3>
                        <span className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(webhook.status)}`}>
                          {webhook.status}
                        </span>
                        <span className={`text-xs ${healthStatus.color}`}>
                          {healthStatus.text}
                        </span>
                      </div>
                      
                      {/* URL */}
                      <div className="flex items-center space-x-2 mb-3">
                        <code className="bg-light-bg-tertiary dark:bg-dark-bg-tertiary px-3 py-2 rounded font-mono text-sm text-light-text-primary dark:text-dark-text-primary border border-light-border dark:border-dark-border truncate">
                          {webhook.url}
                        </code>
                        <button
                          onClick={() => window.open(webhook.url, '_blank')}
                          className="p-2 text-light-text-secondary dark:text-dark-text-secondary hover:text-light-text-primary dark:hover:text-dark-text-primary"
                          title="Open URL"
                        >
                          <ExternalLink size={16} />
                        </button>
                      </div>

                      {/* Secret */}
                      <div className="flex items-center space-x-2 mb-3">
                        <code className="bg-light-bg-tertiary dark:bg-dark-bg-tertiary px-3 py-2 rounded font-mono text-sm text-light-text-primary dark:text-dark-text-primary border border-light-border dark:border-dark-border">
                          {visibleSecrets.has(webhook.id) ? webhook.secret : maskSecret(webhook.secret)}
                        </code>
                        <button
                          onClick={() => toggleSecretVisibility(webhook.id)}
                          className="p-2 text-light-text-secondary dark:text-dark-text-secondary hover:text-light-text-primary dark:hover:text-dark-text-primary"
                          title={visibleSecrets.has(webhook.id) ? 'Hide secret' : 'Show secret'}
                        >
                          {visibleSecrets.has(webhook.id) ? <EyeOff size={16} /> : <Eye size={16} />}
                        </button>
                        <button
                          onClick={() => copySecret(webhook.secret, webhook.id)}
                          className="p-2 text-light-text-secondary dark:text-dark-text-secondary hover:text-light-text-primary dark:hover:text-dark-text-primary"
                          title="Copy secret"
                        >
                          {copiedSecret === webhook.id ? (
                            <CheckCircle size={16} className="text-success" />
                          ) : (
                            <Copy size={16} />
                          )}
                        </button>
                      </div>
                      
                      {/* Events */}
                      <div className="mb-3">
                        <p className="text-sm text-light-text-secondary dark:text-dark-text-secondary mb-2">
                          Subscribed Events:
                        </p>
                        <div className="flex flex-wrap gap-2">
                          {webhook.events.map((event, index) => (
                            <span
                              key={index}
                              className="px-2 py-1 bg-primary-50 dark:bg-primary-900/20 text-primary-600 dark:text-primary-400 text-xs rounded-md"
                            >
                              {event}
                            </span>
                          ))}
                        </div>
                      </div>
                      
                      {/* Metadata */}
                      <div className="flex items-center space-x-6 text-sm text-light-text-secondary dark:text-dark-text-secondary">
                        <div>Created {new Date(webhook.createdAt).toLocaleDateString()}</div>
                        <div>Retry: {webhook.retryCount}x</div>
                        <div>Timeout: {webhook.timeoutMs}ms</div>
                        {webhook.lastSuccessAt && (
                          <div>Last success: {new Date(webhook.lastSuccessAt).toLocaleString()}</div>
                        )}
                      </div>
                    </div>
                    
                    {/* Actions */}
                    <div className="flex items-center space-x-2 ml-4">
                      <button 
                        className="p-2 text-light-text-secondary dark:text-dark-text-secondary hover:text-primary-600 hover:bg-primary-50 dark:hover:bg-primary-900/20 rounded"
                        title="Edit Webhook"
                      >
                        <Settings size={16} />
                      </button>
                      <button 
                        onClick={() => handleDeleteWebhook(webhook.id)}
                        className="p-2 text-light-text-secondary dark:text-dark-text-secondary hover:text-error hover:bg-red-50 dark:hover:bg-red-900/20 rounded"
                        title="Delete Webhook"
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Create Webhook Modal */}
      <CreateWebhookModal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onCreateWebhook={handleCreateWebhook}
      />
    </div>
  );
};